import React from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import { PermissionsProvider } from 'app/providers/PermissionsProvider';
import { VendorOnlyProvider } from 'app/providers/VendorOnlyProvider';
import { TierProvider } from 'app/providers/TierProvider';
import { ThemeProvider } from 'app/providers/ThemeProvider';
import { useVisibleAdminNav } from 'app/registry/adminNavRegistry';
import { cn } from 'shared/lib/cn';
import type { AppUser } from 'shared/state/types';

function AdminNav() {
  const items = useVisibleAdminNav();
  return (
    <nav aria-label="Admin" className="flex flex-col gap-1">
      {items.map(({ id, label, to, icon: Icon }) => (
        <NavLink
          key={id}
          to={to}
          end={to === '/admin'}
          className={({ isActive }) => cn(
            'flex items-center gap-2 rounded-[var(--radius-2)] px-2 py-1.5 font-body text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
            isActive ? 'bg-brand/15 text-brand-strong' : 'text-ink-muted hover:bg-surface-high hover:text-ink',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </NavLink>
      ))}
      <Link to="/" className="mt-6 font-body text-xs text-ink-muted hover:text-ink">← Configurator</Link>
    </nav>
  );
}

/**
 * Admin keeps its own routes and RequireGroup guards (admin/adminRoutes.jsx);
 * this is the V2 skin plus the providers the subtree needs. TierProvider is
 * the one that actually needs the vendor context (it calls useVendorCtx and
 * supplies the AbilityProvider the nav reads); ThemeProvider is mounted so
 * this subtree matches the V2 provider stack and so a vendor-scoped or
 * themed rule would apply if one is ever added — today it writes
 * `data-vendor`/`data-theme` on `<html>` but nothing in the repo selects on
 * `[data-theme]`, so on /admin it currently selects nothing. Content pages
 * are not restyled here.
 *
 * AdminLayout already receives `user` from adminRoutes.jsx, and RequireGroup
 * wraps it there, so the PermissionsProvider mounted below is a second
 * useUserPermissions call on this subtree (RequireGroup.jsx is the first) —
 * the same harmless duplication `/` already has. It goes away when the
 * classic shell is removed and the providers move up (tracked separately).
 */
export function AdminLayout({ user }: { user: AppUser }) {
  return (
    <PermissionsProvider user={user}>
      <VendorOnlyProvider>
        <TierProvider>
          <ThemeProvider>
            <div className="flex min-h-screen bg-ground font-body text-ink">
              <aside className="w-56 shrink-0 border-r border-hairline/15 bg-surface p-4">
                <h2 className="mb-4 font-display text-sm font-bold text-brand-strong">Admin</h2>
                <AdminNav />
              </aside>
              <main className="flex-1 p-6"><Outlet /></main>
            </div>
          </ThemeProvider>
        </TierProvider>
      </VendorOnlyProvider>
    </PermissionsProvider>
  );
}

export default AdminLayout;
